import {useEffect, useState} from "react";
import {getCenters} from "../mongo-db/centers";
import {getFormParams} from "../mongo-db/form-params";
import {getLocal, putLocal} from "../helpers/local-storage";

const LOCAL_KEY_AREAS = "sso_areas_cache";
const LOCAL_KEY_PARAMS = "sso_form_params_cache";

/**
 * Hook que carga áreas/centros y parámetros de formulario desde las nuevas colecciones,
 * filtrados por templateId. Cachea en localStorage para uso offline.
 */
const useFormParams = (templateId) => {
  const tid = Number(templateId);

  const [areas, setAreas] = useState([]);
  const [flatCenters, setFlatCenters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState([]);
  const [findingCategories, setFindingCategories] = useState([]);
  const [findingCriticality, setFindingCriticality] = useState([]);
  const [yesOrNo, setYesOrNo] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]);
  const [itemsMachine, setItemsMachine] = useState([]);
  const [states, setStates] = useState([]);
  const [ssoMachines, setSsoMachines] = useState([]);
  const [ssoQuestions, setSsoQuestions] = useState([]);
  const [executionTypes, setExecutionTypes] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [ready, setReady] = useState(false);

  const applyAreasData = (areasData) => {
    const _areas = areasData.map(a => ({id: a.id, title: a.title}));
    const _flat = areasData.flatMap(a =>
      (a.centers || []).map(c => ({id: c.id, title: c.title, areaId: a.id}))
    );
    setAreas(_areas);
    setFlatCenters(_flat);
  };

  const applyParamsData = (paramsData) => {
    const getItems = (type, extraFields) => {
      const doc = paramsData.find(p => p.type === type);
      return (doc?.items || []).map(i => {
        const base = {id: i.id, title: i.title};
        if (i._id) base._id = i._id;
        if (extraFields) {
          for (const f of extraFields) {
            if (i[f] !== undefined) base[f] = i[f];
          }
        }
        return base;
      });
    };
    setCategories(getItems("category"));
    setResults(getItems("result"));
    setFindingCategories(getItems("findingCategory"));
    setFindingCriticality(getItems("findingCriticality"));
    setYesOrNo(getItems("yesOrNo"));
    setMachineTypes(getItems("machineType"));
    setItemsMachine(getItems("itemsMachine"));
    setStates(getItems("states"));
    setSsoMachines(getItems("ssoMachine", ["areaId"]));
    setSsoQuestions(getItems("ssoQuestion", ["machineId"]));
    setExecutionTypes(getItems("executionType"));
    setWorkTypes(getItems("workType"));
  };

  useEffect(() => {
    if (!tid) return;

    // 1. Cargar cache local inmediatamente
    const cachedAreas = getLocal(LOCAL_KEY_AREAS, null);
    const cachedParams = getLocal(LOCAL_KEY_PARAMS, null);

    if (cachedAreas) {
      const filtered = filterAreasByTemplate(cachedAreas, tid);
      applyAreasData(filtered);
    }
    if (cachedParams) {
      const filtered = filterParamsByTemplate(cachedParams, tid);
      applyParamsData(filtered);
    }
    if (cachedAreas && cachedParams) setReady(true);

    // 2. Fetch online y actualizar cache
    if (navigator.onLine) {
      Promise.all([getCenters(), getFormParams()])
        .then(([areasData, paramsData]) => {
          if (areasData?.length) {
            putLocal(LOCAL_KEY_AREAS, areasData);
            applyAreasData(filterAreasByTemplate(areasData, tid));
          }
          if (paramsData?.length) {
            putLocal(LOCAL_KEY_PARAMS, paramsData);
            applyParamsData(filterParamsByTemplate(paramsData, tid));
          }
          setReady(true);
        })
        .catch(err => {
          console.error("[useFormParams] Error fetching:", err);
          setReady(true);
        });
    } else {
      setReady(true);
    }
  }, [tid]);

  return {
    areas, flatCenters, categories, results, findingCategories, findingCriticality, yesOrNo,
    machineTypes, itemsMachine, states, ssoMachines, ssoQuestions, executionTypes, workTypes,
    ready,
  };
};

/** Filtra áreas y sus centers que tengan el templateId asignado */
function filterAreasByTemplate(areasData, tid) {
  return areasData
    .map(a => ({
      ...a,
      centers: (a.centers || []).filter(c => c.templateIds?.includes(tid)),
    }))
    .filter(a => a.centers.length > 0);
}

/** Filtra items de cada tipo que tengan el templateId asignado */
function filterParamsByTemplate(paramsData, tid) {
  return paramsData.map(p => ({
    ...p,
    items: (p.items || []).filter(i => i.templateIds?.includes(tid)),
  }));
}

export default useFormParams;
